import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { assistantApi } from "@/features/assistant/api";
import {
  useAssistantConversation,
  useAssistantConversations,
} from "@/features/assistant/use-assistant-conversations";
import { useAssistantStream } from "@/features/assistant/use-assistant-stream";

import { Composer } from "./Composer";
import { MessageList } from "./MessageList";
import { Suggestions } from "./Suggestions";

interface Props {
  conversationId: number | null;
  onConversationCreated: (id: number) => void;
}

export function AssistantChat({
  conversationId,
  onConversationCreated,
}: Props) {
  const queryClient = useQueryClient();
  const { data: conversation } = useAssistantConversation(conversationId);
  const { create } = useAssistantConversations();
  const stream = useAssistantStream(conversationId);
  const [creating, setCreating] = useState(false);

  const send = async (content: string) => {
    if (conversationId !== null) {
      await stream.send(content);
      return;
    }
    if (creating) return;
    setCreating(true);
    try {
      // Primeira mensagem: cria a conversa e envia com o id explícito, sem esperar o re-render.
      const created = await create();
      onConversationCreated(created.id);
      await stream.send(content, created.id);
    } catch {
      toast.error("Não foi possível iniciar a conversa.");
    } finally {
      setCreating(false);
    }
  };

  const feedback = useCallback(
    async (messageId: number, value: "up" | "down") => {
      try {
        await assistantApi.sendFeedback(messageId, value);
        await queryClient.invalidateQueries({
          queryKey: ["assistant", "conversation", conversationId],
        });
      } catch {
        toast.error("Não foi possível registrar o feedback.");
      }
    },
    [conversationId, queryClient],
  );

  return (
    <div className="flex h-full flex-col" data-testid="assistant-chat">
      <MessageList
        messages={conversation?.messages ?? []}
        pending={stream.pending}
        pendingUser={stream.pendingUser}
        onFeedback={feedback}
        onRetry={stream.retry}
        emptyState={<Suggestions onPick={send} disabled={creating} />}
      />
      <Composer
        disabled={stream.isStreaming || creating}
        quota={stream.quota}
        onSend={send}
      />
    </div>
  );
}
